import { useState, useCallback } from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import {
  ArrowLeft,
  ClipboardList,
  Clock,
  DollarSign,
  Star,
  Plus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@mustauto/ui/web';
import { maskPhone } from '@mustauto/utils';
import { formatReais } from '@/lib/utils';
import { useTeamStats, type MechanicStats } from '@/hooks/useTeamStats';
import { useServiceOrders } from '@/hooks/useServiceOrders';
import {
  useMechanicPayments,
  useCommissionSummary,
  useCreatePayment,
  useMarkPaid,
} from '@/hooks/useMechanicHR';
import { useCan } from '@/hooks/useCan';

type Tab = 'performance' | 'history' | 'payments';

function getCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function MechanicDetailPage() {
  const { mechanicId } = useParams({ strict: false }) as { mechanicId: string };
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: string };
  const initialTab: Tab =
    search.tab === 'history' ? 'history' : search.tab === 'payments' ? 'payments' : 'performance';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [month] = useState(getCurrentMonth);

  const { data: teamData, isLoading: teamLoading } = useTeamStats(month);
  const mechanic = teamData?.mechanics.find((m) => m.id === mechanicId) ?? null;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'performance', label: 'Desempenho' },
    { key: 'history', label: 'Histórico OS' },
    { key: 'payments', label: 'Pagamentos' },
  ];

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!mechanic) {
    return (
      <div className="flex flex-col items-center gap-4 py-32 text-muted-foreground">
        <p>Mecânico não encontrado.</p>
        <button
          onClick={() => navigate({ to: '/team' })}
          className="text-sm text-primary underline"
        >
          Voltar para Equipe
        </button>
      </div>
    );
  }

  const initial = mechanic.name.charAt(0).toUpperCase();

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Equipe', href: '/team' },
          { label: mechanic.name },
        ]}
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
          {initial}
        </div>
        <div>
          <h1 className="text-2xl font-bold">{mechanic.name}</h1>
          {mechanic.phone && (
            <p className="text-sm text-muted-foreground">{maskPhone(mechanic.phone)}</p>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'performance' && <PerformanceTab mechanic={mechanic} />}
      {activeTab === 'history' && <HistoryTab mechanicId={mechanicId} />}
      {activeTab === 'payments' && <PaymentsTab mechanicId={mechanicId} month={month} />}
    </div>
  );
}

// ========================================
// Aba 1 — Desempenho
// ========================================

function PerformanceTab({ mechanic }: { mechanic: MechanicStats }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatCard
        icon={<ClipboardList size={16} />}
        label="OS Ativas Hoje"
        value={String(mechanic.activeToday)}
      />
      <StatCard
        icon={<ClipboardList size={16} />}
        label="Concluídas no Mês"
        value={String(mechanic.completedMonth)}
      />
      <StatCard
        icon={<DollarSign size={16} />}
        label="Faturamento"
        value={mechanic.revenue == null ? '—' : formatReais(mechanic.revenue)}
      />
      <StatCard
        icon={<DollarSign size={16} />}
        label="Ticket Médio"
        value={mechanic.avgTicket == null ? '—' : formatReais(mechanic.avgTicket)}
      />
      <StatCard
        icon={<Clock size={16} />}
        label="Tempo Médio"
        value={`${mechanic.avgExecutionHours.toFixed(1)}h`}
      />
      {mechanic.avgRating !== null && (
        <StatCard
          icon={<Star size={16} />}
          label="Avaliação"
          value={mechanic.avgRating.toFixed(1)}
        />
      )}
      <StatCard
        icon={<ClipboardList size={16} />}
        label="Taxa de Retrabalho"
        value={`${Math.round(mechanic.reworkRate * 100)}%`}
        alert={mechanic.reworkRate > 0.15}
      />
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  alert,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  alert?: boolean;
}) {
  return (
    <Card className={alert ? 'border-warning/50' : ''}>
      <CardContent className="flex items-center gap-3 p-4">
        <span className="text-muted-foreground">{icon}</span>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className={`text-lg font-bold ${alert ? 'text-warning' : ''}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ========================================
// Aba 2 — Historico OS
// ========================================

function HistoryTab({ mechanicId }: { mechanicId: string }) {
  const { data, isLoading, isError, error, refetch } = useServiceOrders({
    mechanicId,
    status: 'completed',
    limit: 50,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (isError && !data) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  const orders = data?.orders ?? [];

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhuma OS concluída encontrada.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>OS #</TableHead>
            <TableHead>Placa</TableHead>
            <TableHead>Veículo</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id}>
              <TableCell className="font-medium">{o.number}</TableCell>
              <TableCell className="font-mono">{o.plate}</TableCell>
              <TableCell>{o.vehicle}</TableCell>
              <TableCell>{new Date(o.createdAt).toLocaleDateString('pt-BR')}</TableCell>
              <TableCell>
                <Badge variant="secondary">Concluída</Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ========================================
// Aba 3 — Pagamentos
// ========================================

function PaymentsTab({ mechanicId, month }: { mechanicId: string; month: string }) {
  const year = month.split('-')[0];
  const { data: paymentsData, isLoading: paymentsLoading } = useMechanicPayments(mechanicId, year);
  const { data: commission, isLoading: commissionLoading } = useCommissionSummary(mechanicId, month);
  const markPaid = useMarkPaid(mechanicId);
  const [showAddForm, setShowAddForm] = useState(false);
  // Registrar e dar baixa no pagamento é `rh.gerir` (Fase 3).
  const canManage = useCan()('rh.gerir');

  const payments = paymentsData?.payments ?? [];

  const typeLabel = (type: string) => {
    switch (type) {
      case 'fixed': return 'Fixo';
      case 'commission': return 'Comissão';
      case 'advance': return 'Adiantamento';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Commission summary */}
      {commissionLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : commission ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Resumo de Comissão — {month}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">OS Concluídas</p>
                <p className="text-lg font-bold">{commission.totalOs}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Faturamento Mão de Obra</p>
                <p className="text-lg font-bold">{formatReais(commission.totalRevenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Taxa de Comissão</p>
                <p className="text-lg font-bold">{(commission.commissionRate * 100).toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Valor Comissão</p>
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  {formatReais(commission.commissionAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Já Pago</p>
                <p className="text-lg font-bold">{formatReais(commission.alreadyPaid)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Saldo</p>
                <p className={`text-lg font-bold ${commission.balance > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {formatReais(commission.balance)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Add payment button */}
      {canManage && <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          {showAddForm ? 'Cancelar' : 'Registrar Pagamento'}
        </button>
      </div>}

      {/* Add form modal */}
      {showAddForm && (
        <PaymentForm
          mechanicId={mechanicId}
          onDone={() => setShowAddForm(false)}
        />
      )}

      {/* Payments table */}
      {paymentsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : payments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhum pagamento registrado.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-center">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">
                    {new Date(p.periodStart + 'T12:00:00').toLocaleDateString('pt-BR')} —{' '}
                    {new Date(p.periodEnd + 'T12:00:00').toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{typeLabel(p.type)}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatReais(Number(p.amount))}
                  </TableCell>
                  <TableCell>
                    {p.paidAt ? (
                      <Badge variant="default">Pago</Badge>
                    ) : (
                      <Badge variant="warning">Pendente</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.description ?? '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {!p.paidAt && canManage && (
                      <button
                        onClick={() => markPaid.mutate(p.id)}
                        disabled={markPaid.isPending}
                        className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors disabled:opacity-50"
                      >
                        Marcar Pago
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function PaymentForm({
  mechanicId,
  onDone,
}: {
  mechanicId: string;
  onDone: () => void;
}) {
  const createPayment = useCreatePayment(mechanicId);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'fixed' | 'commission' | 'advance'>('commission');
  const [description, setDescription] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await createPayment.mutateAsync({
        periodStart,
        periodEnd,
        amount: Number(amount),
        type,
        description: description || undefined,
      });
      onDone();
    },
    [periodStart, periodEnd, amount, type, description, createPayment, onDone],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="mx-4 w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Registrar Pagamento</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Início do período *</label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  required
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Fim do período *</label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  required
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Valor (R$) *</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground">Tipo *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as typeof type)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="commission">Comissão</option>
                  <option value="fixed">Fixo</option>
                  <option value="advance">Adiantamento</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Descrição</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                placeholder="Opcional"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={createPayment.isPending || !periodStart || !periodEnd || !amount}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {createPayment.isPending ? 'Salvando...' : 'Registrar'}
              </button>
              <button
                type="button"
                onClick={onDone}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
